<script setup lang="ts">
/**
 * Лента комментариев и композер — макет 13:462: аватар 24, имя, время, тело
 * markdown; внизу панель редактора и поле ввода.
 *
 * Порядок — снизу вверх по времени, как в переписке: последнее сообщение
 * стоит у поля ввода, а не улетает наверх ленты.
 *
 * Свои комментарии правятся и удаляются, чужие — нет: так же считает и
 * сервис, и кнопка, которая всегда кончается отказом, хуже её отсутствия.
 */
import { AppAvatar, AppButton } from '@vinprop/design-system'
import { AppRichTextEditor } from '@vinprop/design-system/editor'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useAuthStore } from '@/features/auth/store'
import { collectMentionIds, useTrackerEditorExtensions } from '@/features/editor'
import { formatStamp } from '@/features/issues/format'

import {
  createComment,
  deleteComment,
  listComments,
  updateComment,
  type Comment,
} from '../comments'
import { useIssueStore } from '../store'

const emit = defineEmits<{ failed: [error: unknown]; counted: [total: number] }>()

const { t, locale } = useI18n()
const auth = useAuthStore()
const store = useIssueStore()

const extensions = useTrackerEditorExtensions(() => store.issue?.projectId ?? '')

const comments = ref<Comment[]>([])
const total = ref(0)
const page = ref(1)
const isLoading = ref(false)

const draft = ref('')
const isSending = ref(false)

/** Какой комментарий сейчас правят: пусто — ни один */
const editingId = ref('')
const editingDraft = ref('')

const issueId = computed(() => store.issue?.id ?? '')

const hasMore = computed(() => comments.value.length < total.value)

const load = async (next = false) => {
  if (!issueId.value) return

  isLoading.value = true
  page.value = next ? page.value + 1 : 1

  try {
    const chunk = await listComments(issueId.value, page.value)

    comments.value = next ? [...chunk.comments, ...comments.value] : chunk.comments
    total.value = chunk.total
    emit('counted', chunk.total)
  } catch (failure) {
    emit('failed', failure)
  } finally {
    isLoading.value = false
  }
}

const send = async () => {
  const body = draft.value.trim()

  if (!body) return

  isSending.value = true

  try {
    const created = await createComment(issueId.value, body, collectMentionIds(body))

    if (created) {
      comments.value = [...comments.value, created]
      total.value += 1
      emit('counted', total.value)
    }

    draft.value = ''
  } catch (failure) {
    emit('failed', failure)
  } finally {
    isSending.value = false
  }
}

const edit = (comment: Comment) => {
  editingId.value = comment.id
  editingDraft.value = comment.bodyMd
}

const saveEdit = async () => {
  const body = editingDraft.value.trim()

  if (!body) return

  try {
    const saved = await updateComment(editingId.value, body, collectMentionIds(body))

    comments.value = comments.value.map((entry) =>
      entry.id === editingId.value ? (saved ?? { ...entry, bodyMd: body }) : entry,
    )
    editingId.value = ''
  } catch (failure) {
    emit('failed', failure)
  }
}

const remove = async (comment: Comment) => {
  // Подтверждения нет намеренно: удаляют свой же комментарий, и лишнее окно
  // на каждое такое действие раздражает сильнее, чем спасает.
  try {
    await deleteComment(comment.id)

    comments.value = comments.value.filter((entry) => entry.id !== comment.id)
    total.value = Math.max(total.value - 1, 0)
    emit('counted', total.value)
  } catch (failure) {
    emit('failed', failure)
  }
}

const stamp = (comment: Comment) =>
  formatStamp(comment.createdAt, locale.value, {
    today: t('issue.today'),
    yesterday: t('issue.yesterday'),
  })

const isMine = (comment: Comment) => comment.authorId === auth.user?.id

onMounted(() => load())

// Соседняя задача открывается тем же экраном: маршрут меняется, компонент нет.
watch(issueId, () => load())
</script>

<template>
  <section class="icmt">
    <button v-if="hasMore" type="button" class="icmt__more" @click="load(true)">
      {{ t('issue.comments.more') }}
    </button>

    <p v-if="!comments.length && !isLoading" class="icmt__empty">
      {{ t('issue.comments.empty') }}
    </p>

    <article v-for="comment in comments" :key="comment.id" class="cmt">
      <AppAvatar :name="comment.authorName" :size="24" />

      <div class="cmt__body">
        <div class="cmt__head">
          <span class="cmt__author">{{ comment.authorName }}</span>
          <span class="cmt__time">{{ stamp(comment) }}</span>

          <template v-if="isMine(comment) && editingId !== comment.id">
            <button type="button" class="cmt__action" @click="edit(comment)">
              {{ t('issue.comments.edit') }}
            </button>
            <button type="button" class="cmt__action" @click="remove(comment)">
              {{ t('issue.comments.remove') }}
            </button>
          </template>
        </div>

        <template v-if="editingId === comment.id">
          <AppRichTextEditor
            v-model="editingDraft"
            preset="comment"
            autofocus
            :extensions="extensions"
          />
          <div class="cmt__actions">
            <AppButton variant="primary" size="small" @click="saveEdit">
              {{ t('settings.save') }}
            </AppButton>
            <AppButton variant="secondary" size="small" @click="editingId = ''">
              {{ t('settings.cancel') }}
            </AppButton>
          </div>
        </template>

        <AppRichTextEditor
          v-else
          class="cmt__text"
          :model-value="comment.bodyMd"
          readonly
          :extensions="extensions"
        />
      </div>
    </article>

    <div class="icmt__composer">
      <AppRichTextEditor
        v-model="draft"
        preset="comment"
        :extensions="extensions"
        :placeholder="t('issue.comments.placeholder')"
      />

      <AppButton
        class="icmt__send"
        variant="primary"
        size="small"
        :disabled="!draft.trim()"
        :loading="isSending"
        @click="send"
      >
        {{ t('issue.comments.send') }}
      </AppButton>
    </div>
  </section>
</template>

<style scoped>
.icmt {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.icmt__more {
  align-self: flex-start;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.icmt__empty {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.cmt {
  display: flex;
  gap: 10px;
}

.cmt__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 4px;
  min-width: 0;
}

.cmt__head {
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 16px;
}

.cmt__author {
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
}

.cmt__time {
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
}

/* Правка и удаление показываются только у своих и только под курсором: в
   ленте из десяти сообщений двадцать кнопок читаются как список действий. */
.cmt__action {
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-overline);
  cursor: pointer;
  opacity: 0;
}

.cmt:hover .cmt__action,
.cmt__action:focus-visible {
  opacity: 1;
}

.cmt__action:hover {
  color: var(--color-text-primary);
}

.cmt__text {
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
}

.cmt__actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.icmt__composer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
}

.icmt__send {
  align-self: flex-start;
}
</style>
